import { CaretUpDownIcon } from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { useEventProducts, useEventStations } from '@/modules/eventsV2/api';
import { EventProduct } from '@/modules/eventsV2/types';

import { InventoryAmountInput, InventoryRemoveButton } from '../components';
import type { EventFiltersType } from '@/modules/eventsV2/hooks';

export const useInventoryTab = (filters: EventFiltersType) => {
  const params = useParams<{ id: string }>();
  const eventId = params.id;

  const queryClient = useQueryClient();

  const [isUploading, setIsUploading] = useState(false);
  const [selectedStationId, setSelectedStationId] = useState<string>('');

  const { stationsQuery } = useEventStations(eventId, true);

  const {
    isFetching,
    products,
    uploadEventProducts,
    getGenerateReportUrl,
    isGeneratingReport,
  } = useEventProducts(eventId, selectedStationId, filters);

  const uploadProducts = async (file: File) => {
    setIsUploading(true);
    try {
      await uploadEventProducts(file);
      await queryClient.invalidateQueries({
        queryKey: ['eventProducts', eventId, selectedStationId],
      });
      toast.success('Inventory uploaded successfully');
      setIsUploading(false);
    } finally {
      setIsUploading(false);
    }
  };

  const pickFileAndUpload = async (event) => {
    if (!event.target.files.length) return;

    const file = event.target.files[0];

    await uploadProducts(file);
  };

  useEffect(() => {
    if (!stationsQuery.data) {
      return;
    }

    if (!stationsQuery.data.length) {
      return;
    }

    setSelectedStationId(stationsQuery.data[0].id);
  }, [stationsQuery.data]);

  const columns: ColumnDef<EventProduct>[] = [
    {
      size: 200,
      accessorKey: 'category',
      header: ({ column }) => {
        return (
          <Button
            type="button"
            variant="transparent"
            className="p-0 uppercase text-sm font-bold"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Category
            <CaretUpDownIcon />
          </Button>
        );
      },
      cell: ({ row }) => (
        <span className="line-clamp-1">{row.original.category}</span>
      ),
    },
    {
      size: 200,
      accessorKey: 'name',
      header: ({ column }) => {
        return (
          <Button
            type="button"
            variant="transparent"
            className="p-0 uppercase text-sm font-bold"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Name
            <CaretUpDownIcon />
          </Button>
        );
      },
      cell: ({ row }) => (
        <span className="line-clamp-2">{row.original.name}</span>
      ),
    },
    {
      size: 200,
      accessorKey: 'nickname',
      header: ({ column }) => {
        return (
          <Button
            type="button"
            variant="transparent"
            className="p-0 uppercase text-sm font-bold"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Nickname
            <CaretUpDownIcon />
          </Button>
        );
      },
      cell: ({ row }) => (
        <span className="line-clamp-1">{row.original.nickname}</span>
      ),
    },
    {
      size: 200,
      accessorKey: 'styleNumber',
      header: 'Style Number',
    },
    {
      size: 200,
      accessorKey: 'size',
      header: 'Size',

      cell: ({ row }) => {
        return (
          <span className="line-clamp-1">
            {row.original?.size?.size || 'Unknown'}
          </span>
        );
      },
    },
    {
      accessorKey: 'totalAmount',
      header: 'Total Amount',
      cell: ({ row }) => (
        <InventoryAmountInput
          eventId={eventId}
          stationId={selectedStationId}
          productId={row.original.id}
          sizeId={row.original?.size?.id}
          defaultValue={row.original.totalAmount.toString()}
        />
      ),
    },
    {
      size: 40,
      id: 'actions',
      header: 'Action',
      enableHiding: false,
      cell: ({ row }) => (
        <InventoryRemoveButton
          eventId={eventId}
          stationId={selectedStationId}
          productId={row.original.id}
          sizeId={row.original?.size?.id}
        />
      ),
    },
  ];

  return {
    eventId,
    columns,
    products,
    isFetching,
    isUploading,
    isGeneratingReport,
    stationsQuery,
    selectedStationId,
    getGenerateReportUrl,
    pickFileAndUpload,
    setSelectedStationId,
  };
};
